import React, { FC, useRef, useState } from "react"
import { Tooltip } from "react-tooltip"
import { GatsbyImage } from "gatsby-plugin-image"

import { addThousandCommas, calculateMonthlyPriceWithAnnualDiscount, generateConditionalClassName, roundToThousandsWithDecimal } from "@utils"

import Button, { BUTTON_VARIANTS } from "@components/Button/Button"
import { Icon, ICON_TYPES } from "@helpscout/hs-marketing-shared-components"

import { PricingHeroPlanDIV } from "./PricingHeroPlan.styles"
import { PricingHeroPlanFragmentFragment } from "@graphqlTypes"

type PricingHeroPlanProps = {
  annualDiscountPercentage: number,
  baseContacts: number,
  currentIncrement: number,
  hideButton: boolean
  isAnnualDiscountApplied: boolean,
  isCappedOut?: boolean,
  maxContacts: number,
  prices: { [key: string]: number | null },
  onClick?: () => void,
}

const PricingHeroPlan: FC<PricingHeroPlanFragmentFragment & PricingHeroPlanProps> = ({
  annualDiscountPercentage,
  baseContacts,
  ctaLink,
  ctaText,
  currentIncrement,
  features,
  featuresIntro,
  hideButton,
  id,
  isAnnualDiscountApplied,
  maxContacts,
  name,
  onClick,
  prices,
}) => {
  const elRef = useRef<HTMLDivElement | null>(null)

  const isFree = id === "free"
  const isPro = id === "pro"
  const isCappedOut = maxContacts !== null && currentIncrement > maxContacts

  const currentPrice = isCappedOut ?
    (prices[`_${maxContacts}`] || 0) :
    (prices[`_${currentIncrement}`] || 0)
  const priceToDisplay = isAnnualDiscountApplied ?
    calculateMonthlyPriceWithAnnualDiscount(currentPrice, annualDiscountPercentage) :
    currentPrice

  return (
    <PricingHeroPlanDIV
      className={`plan--${id}`}
      onClick={onClick}
      ref={elRef}
    >
      <div className="gradient-background" />
      <div className="plan-details">
        <h3 className="type--headline-medium-M margin-bottom--base-1-5">{name}</h3>
        <div className="plan-price margin-bottom--base-1-5">
          {isPro && !isAnnualDiscountApplied ? (
            <span
              className="plan-price--annual type--headline-medium-4XL font-weight--medium color--text-headline"
            >
              Annual only
            </span>
          ) : (
            <>
              <span
                className="plan-price--value type--headline-medium-5XL font-weight--medium color--text-headline"
              >
                ${roundToThousandsWithDecimal(priceToDisplay)}
              </span>
              {!isFree && (
                <span className="plan-price--mo type--text-XS color--text-default">/month</span>
              )}
            </>
          )}
        </div>
        <p className="plan-description type--text-S line-height--snug color--text-headline margin-bottom--0">
          <strong>
            {
              (maxContacts !== null && currentIncrement > maxContacts) ?
                `Up to ${addThousandCommas(maxContacts)} contacts` :
                currentIncrement < baseContacts ?
                  `Starts with ${addThousandCommas(baseContacts)} contacts` :
                  `For ${addThousandCommas(currentIncrement)} contacts`
            }
            {" "}
            per month
          </strong>
          <br />
          and unlimited users
        </p>
      </div >
      <div className="plan-features padding-bottom--base-0-5">
        {featuresIntro && <h6 className="type--headline-medium-S margin-bottom--XS">{featuresIntro}</h6>}
        <ul>
          {features?.map((feature, i) => !feature ? null : (
            <li
              className="plan-feature"
              key={feature.text}
            >
              <Icon
                color="var(--color--cobalt-600)"
                size="calc(var(--font-size--S) * var(--line-height--relaxed))"
                type={ICON_TYPES["checkmark"]}
              />
              <span
                data-tooltip-id={feature.tooltip ? `Tooltip--${id}-feature-${i}` : undefined}
                className="feature-description type--text-S line-height--relaxed margin-bottom--0"
              >
                <span
                  // className={feature.tooltip ? "info-popover" : ""}
                  dangerouslySetInnerHTML={{ __html: feature.text || "" }}
                />
              </span>
            </li>
          ))}
          <li
            className="plan-feature type--text-S line-height--relaxed"
          >
            <Icon
              color="var(--color--cobalt-600)"
              size="calc(var(--font-size--S) * var(--line-height--relaxed))"
              type={ICON_TYPES["plus"]}
            />
            <a
              className="feature-description is-more-link font-weight--medium"
              href="#compare-plans-and-features"
            >
              and more
            </a>
          </li>
        </ul>
      </div>
      {
        !hideButton && (
          <Button
            className="cta-button"
            href={ctaLink || "/"}
            variant={isFree ? BUTTON_VARIANTS.SECONDARY : BUTTON_VARIANTS.PRIMARY}
          >
            {ctaText || "Try for free"}
          </Button>
        )
      }
      {
        features?.map((feature, i) => feature && feature.tooltip && (
          <Tooltip
            className={generateConditionalClassName({
              "font-family--sans font-size--S": true,
              "with-visual": !!feature.tooltipImage,
            })}
            float
            key={feature.tooltip}
            id={`Tooltip--${id}-feature-${i}`}
            place="top"
          >
            {feature.tooltipImage && (
              <div className="visual">
                <GatsbyImage
                  alt={feature.tooltip || ""}
                  image={feature.tooltipImage}
                />
              </div>
            )}
            <span dangerouslySetInnerHTML={{ __html: feature.tooltip || "" }} />
          </Tooltip>
        ))
      }
    </PricingHeroPlanDIV >
  )
}

export default PricingHeroPlan
